import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { onAnimationEnd } from '@theme/utilities';
import { ConfiguratorStateEvent, renderPrice } from '@theme/configurator-shared';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} summaryDisplay - aria-live wrapper around the pills/price summary
 * @property {HTMLElement} pillsDisplay - Container for the configurator option-summary pills
 * @property {HTMLElement} priceDisplay - Price text container
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    'stickyBar',
    'addToCartButton',
    'quantityDisplay',
    'quantityNumber',
    'summaryDisplay',
    'pillsDisplay',
    'priceDisplay',
  ];

  /** @type {IntersectionObserver | null} */
  #configuratorIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ConfiguratorStateEvent.eventName, this.#handleConfiguratorState, { signal });

    this.#getInitialQuantity();

    // Custom-element connection order across separate elements on the page
    // isn't guaranteed — the configurator may connect and dispatch its first
    // configurator:state event before this element has registered the
    // listener above. This eagerly pulls whatever state the configurator has
    // ALREADY rendered (a no-op if it hasn't connected yet, in which case the
    // listener above catches its first event normally), mirroring the same
    // fix already applied for this exact race in
    // assets/laken-recommendation.js's #syncFromConfigurator.
    this.#syncFromConfigurator();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#configuratorIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the configurator's visibility
   */
  #setupIntersectionObserver() {
    const configuratorElement = this.#getConfiguratorElement();
    if (!configuratorElement) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for configurator visibility
    this.#configuratorIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = configuratorElement.getBoundingClientRect();
          // Only show if the configurator is above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#configuratorIntersectionObserver.observe(configuratorElement);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = this.#getConfiguratorAddToCartButton();
  }

  // Public action handlers
  /**
   * Opens the options modal (assets/sticky-configurator-modal.js — added by
   * a later task in this plan), bound declaratively via
   * sections/product-information.liquid's on:click="/handleOpenModal" on
   * the mobile-only options-summary button. The modal component itself
   * handles portaling the real configurator-variant-picker element in on
   * open / back out on close. Until that later task lands, this method
   * simply no-ops (the `if (modal && ...)` guard below fails safely since
   * no element with this id exists yet).
   */
  handleOpenModal = () => {
    const sectionId = this.#getSectionId();
    if (!sectionId) return;
    const modal = document.getElementById(`StickyConfiguratorDialog-${sectionId}`);
    if (modal && 'openWithConfigurator' in modal && typeof modal.openWithConfigurator === 'function') {
      modal.openWithConfigurator();
    }
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // Re-resolved live (not read from the cached #targetAddToCartButton
    // field) because the configurator-variant-picker element may currently
    // be portaled inside the options modal (a later task) rather than at
    // its original position inside the configurator block —
    // #getConfiguratorAddToCartButton always finds it via
    // document.querySelector scoped by section+product id, which resolves
    // correctly regardless of the element's current parent.
    const targetButton = this.#getConfiguratorAddToCartButton();
    if (!targetButton) return;
    this.#targetAddToCartButton = targetButton;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles the configurator's configurator:state event — the sticky bar's
   * single source of truth for price, pill summary, swatch, and
   * add-to-cart-enabled state. Fired by both the Bettwäsche and generic
   * configurator paths (assets/configurator-variant-picker.js and
   * assets/configurator-generic-picker.js respectively) on every state
   * change, entirely client-side (no fetch).
   * @param {ConfiguratorStateEvent} event
   */
  #handleConfiguratorState = (event) => {
    this.#applyConfiguratorState(event);
  };

  /**
   * One-time, synchronous pull of the configurator's CURRENT state, read
   * directly from its already-rendered DOM — covers the "configurator
   * connects and dispatches before this element's listener is registered"
   * race. No-ops silently if the configurator element isn't present yet or
   * hasn't rendered a price yet, in which case its own connectedCallback
   * will dispatch a configurator:state event that the listener registered
   * in connectedCallback catches normally. Mirrors
   * assets/laken-recommendation.js's #syncFromConfigurator.
   *
   * Scope is deliberately price + swatch only — pills and availability are
   * NOT synced here. Unlike price/swatch, pillsDisplay has no server-
   * rendered fallback to read back from the configurator's DOM (it starts
   * empty in the Liquid markup), so on this race path specifically they
   * stay empty until the next real configurator:state event (typically the
   * shopper's first interaction with the configurator).
   */
  #syncFromConfigurator() {
    const configuratorPicker = this.#getConfiguratorPicker();
    const priceDisplay = configuratorPicker?.querySelector('.configurator-variant-picker__price');
    if (!priceDisplay?.textContent) return;

    // The configurator's own priceDisplay is already-rendered markup (see
    // renderPrice() in assets/configurator-shared.js, called by both
    // #updatePrice in assets/configurator-variant-picker.js and
    // assets/configurator-generic-picker.js) — a `.price` span and,
    // when the resolved selection is on sale, a `.compare-at-price` span
    // after it. Cloning its child nodes (not reading .textContent, which
    // would concatenate both spans with no separator and lose the sale
    // styling) preserves that structure exactly, without re-deriving or
    // duplicating any price-formatting logic here.
    this.refs.priceDisplay.replaceChildren(...Array.from(priceDisplay.childNodes, (node) => node.cloneNode(true)));
    this.#syncSwatchFromConfigurator(configuratorPicker);
  }

  /**
   * Copies the currently-checked Farbe swatch's rendered style straight off
   * the configurator's own DOM (its `<span class="swatch">`'s
   * `--swatch-background` custom property, set by snippets/swatch.liquid),
   * rather than reconstructing swatch color/image data from scratch —
   * configurator:state carries `farbe` as a plain string, not swatch
   * color/image data, and the configurator's own swatch is always
   * authoritative and already in sync with the current Farbe. No-ops if the
   * product has no Farbe-equivalent option (swatchDisplay isn't rendered in
   * that case) or the configurator hasn't rendered a checked Farbe swatch
   * yet.
   * @param {Element | null | undefined} configuratorPicker
   */
  #syncSwatchFromConfigurator(configuratorPicker) {
    const swatchDisplay = this.querySelector('[ref="swatchDisplay"] .swatch');
    if (!swatchDisplay) return;

    // Keep in sync with `.configurator-variant-picker__farbe`
    // (blocks/configurator-variant-picker.liquid) and buildPillOption's
    // swatch markup (assets/configurator-shared.js) — a rename on either
    // side silently stops this from matching rather than erroring.
    const checkedFarbeSwatch = configuratorPicker?.querySelector(
      '.configurator-variant-picker__farbe input:checked + .swatch, .variant-option--swatches input:checked + .swatch'
    );
    if (!(checkedFarbeSwatch instanceof HTMLElement)) return;

    swatchDisplay.setAttribute('style', checkedFarbeSwatch.getAttribute('style') ?? '');
  }

  /**
   * Applies every field carried by a configurator:state event: price (via
   * the same shared renderPrice() helper the configurator itself uses, so
   * a discounted bundle shows the same sale/"was" price treatment here
   * too), the pill summary (rebuilt from scratch each time — cheap, since
   * it's at most a handful of small elements), add-to-cart-button disabled
   * state, and the swatch.
   * @param {ConfiguratorStateEvent} event
   */
  #applyConfiguratorState(event) {
    const { priceCents, compareAtCents, available, pills } = event;

    renderPrice(this.refs.priceDisplay, priceCents, compareAtCents, this.dataset.moneyFormat ?? '', this.dataset.currency ?? 'USD');

    this.refs.pillsDisplay.innerHTML = '';
    for (const pill of pills) {
      const pillEl = document.createElement('span');
      pillEl.className = 'sticky-add-to-cart__pill';

      const labelEl = document.createElement('span');
      labelEl.className = 'sticky-add-to-cart__pill-label';
      labelEl.textContent = `${pill.label} `;

      const valueEl = document.createElement('span');
      valueEl.className = 'sticky-add-to-cart__pill-value';
      valueEl.textContent = pill.value;

      pillEl.append(labelEl, valueEl);
      this.refs.pillsDisplay.append(pillEl);
    }

    this.refs.addToCartButton.disabled = !available;
    this.dataset.variantAvailable = String(available);

    this.#syncSwatchFromConfigurator(this.#getConfiguratorPicker());
  }

  /**
   * Handles ProductSelectEvent (dispatched by the configurator's
   * #updateGallery on a Farbe/option change). This is now used ONLY to
   * update the sticky bar's image: price, pills, and availability come from
   * configurator:state instead (see #handleConfiguratorState), since that
   * event fires synchronously client-side with no fetch, matching the
   * configurator's own resolution model. The old whole-bar morph-from-
   * fetched-HTML approach is removed.
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;
        if (productId && productId !== this.dataset.productId) return;

        const newImage = /** @type {HTMLImageElement | null} */ (
          html.querySelector('sticky-add-to-cart [ref="productImage"]')
        );
        if (newImage?.src) {
          this.refs.productImage.src = newImage.src;
          this.refs.productImage.srcset = newImage.srcset;
          const optionsButtonImage = this.querySelector('[ref="optionsButtonImage"]');
          if (optionsButtonImage instanceof HTMLImageElement) {
            optionsButtonImage.src = newImage.src;
          }
        }

        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * @returns {string | null}
   */
  #getSectionId() {
    const sectionElement = this.closest('.shopify-section');
    return sectionElement ? sectionElement.id.replace('shopify-section-', '') : null;
  }

  /**
   * Finds the configurator block's root element within the current section,
   * the sticky bar's new visibility trigger (replacing the old
   * .buy-buttons-block target — see blocks/_product-configurator.liquid,
   * which renders this id as `ProductConfigurator-{{ section.id }}`).
   * @returns {HTMLElement | null}
   */
  #getConfiguratorElement() {
    const sectionId = this.#getSectionId();
    if (!sectionId) return null;
    return document.getElementById(`ProductConfigurator-${sectionId}`);
  }

  /**
   * Finds this section's <configurator-variant-picker> element by
   * data-product-id, scoped to the current .shopify-section — NOT nested
   * inside #getConfiguratorElement()'s wrapper, because
   * assets/sticky-configurator-modal.js may have portaled the picker out of
   * that wrapper into the options modal (see Task 7). Searching the whole
   * section (which still contains the modal, a sibling of the configurator
   * block) finds it correctly regardless of which of the two spots it
   * currently lives in. Uses the same section-scoped-by-data-product-id
   * `document.querySelector` pattern as the theme's other cross-component
   * lookups.
   * @returns {Element | null}
   */
  #getConfiguratorPicker() {
    const sectionId = this.#getSectionId();
    const productId = this.dataset.productId;
    if (!sectionId || !productId) return null;
    return document.querySelector(
      `#shopify-section-${sectionId} configurator-variant-picker[data-product-id="${productId}"]`
    );
  }

  /**
   * Finds the configurator's own add-to-cart button, the sticky bar's new
   * puppet-click target (replacing the old buy-buttons submit button).
   * @returns {HTMLButtonElement | null}
   */
  #getConfiguratorAddToCartButton() {
    const picker = this.#getConfiguratorPicker();
    if (!picker) return null;
    return picker.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
